import { PARTICIPANT_ACTOR_KEY } from '@dadohr/shared/domainTypes/experience';
import {
  IReportingQueryDrilldown,
  IReportTaskInfoResolved
} from '@dadohr/shared/domainTypes/reports';
import { IconButton, TableContainer, Typography } from '@material-ui/core';
import { compact, flatten, mapValues } from 'lodash';
import { ExternalLink } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  DataTable2Cell,
  DataTable2With2StickyColumns
} from '../../../../../components/DataTable';
import { useErrorLogger } from '../../../../../hooks/useErrorLogger';
import { useUnsafeTranslation } from '../../../../../i18next';
import { useCurrentUser } from '../../../../../services/currentUser';
import { useExperienceTemplateById } from '../../../../../services/experiences/template';
import { useCombineLoadingValues } from '../../../../../services/firebase';
import { toFullName } from '../../../../../services/user';
import {
  getRelativeAnchorDefinition,
  useAnchorFields
} from '../../../../Experiences/components/Anchors';
import { CardItemDialog } from '../../../../Experiences/components/CalendarV2/CardItemDialog';
import { getItemDisplayName } from '../../../../Experiences/helpers/adminName';
import { TimelineItemExecutionTableLazy } from '../../../../Experiences/components/CalendarV2/DialogTable';
import { ParticipantDialogLazy } from '../../../../Experiences/pages/Details/ParticipantDialog';
import { useReportsDrilldownDataByInstance } from '../../../services/data';
import { ReportDrilldownSortKeys } from '../types';
import {
  collectGapsAndSort,
  CompletionPercentageChip,
  DataTable2RowOtherData,
  gapOrAnchorOrOther,
  LEFT_COL_WIDTH,
  NameContainer,
  ReportDrilldownEmptyState,
  ReportDrilldownError,
  ReportDrilldownLoader,
  RowData,
  shortName,
  StatusTile,
  taskStatusToString,
  TrackingFns,
  useApplySearchAndSortRows,
  WithTooltipIfTruncated
} from './common';

export const BodyByParticipant = ({
  q,
  search,
  sort,
  trackingFns
}: {
  q: IReportingQueryDrilldown;
  search: string;
  sort: ReportDrilldownSortKeys;
  trackingFns: TrackingFns;
}) => {
  const type = q.otherData?.type;
  const { org } = useCurrentUser();
  const { t } = useUnsafeTranslation();
  const [vs, loading, error] = useCombineLoadingValues(
    useExperienceTemplateById(q.tplIds[0]),
    useReportsDrilldownDataByInstance(q)
  );
  const [tpl, d] = vs || [undefined, undefined];

  const anchorFields = useAnchorFields(org.customUserFields);
  // we just pass primitives down into the hook, to avoid
  // having a dependency on the template itself, which might change for
  // unrelated reasons
  const anchorDefinition =
    (tpl && getRelativeAnchorDefinition(tpl.data.anchor, anchorFields, t)) ||
    null;
  const anchorPosition = anchorDefinition?.position ?? null;
  const anchorName = anchorDefinition?.shortLabel ?? null;

  const [
    peopleDialogTask,
    setPeopleDialogTask
  ] = useState<null | IReportTaskInfoResolved>(null);

  // Add a lazy dialog instance somehow
  const [dialogInstance, setDialogInstance] = useState<null | {
    tplId: string;
    instanceId: string;
  }>(null);
  const allRows = useMemo<null | RowData[]>(() => {
    if (!d || d.type !== PARTICIPANT_ACTOR_KEY) {
      return null;
    }
    const anchor =
      anchorPosition !== null && anchorName !== null
        ? { name: anchorName, position: anchorPosition }
        : null;
    const sortedTasks = collectGapsAndSort(
      Object.values(d.data.tasks),
      anchor,
      t
    );

    const expTitle = tpl?.data.title || '';
    const resolveToken = (s: string) =>
      s.replace(/\{\{\s*experience\.title\s*\}\}/g, expTitle);
    const resolvedTasksById: {
      [key: string]: IReportTaskInfoResolved;
    } = mapValues(d.data.tasks, (tsk) => {
      return {
        ...tsk,
        title: resolveToken(tsk.title),
        adminName: tsk.adminName != null ? resolveToken(tsk.adminName) : tsk.adminName,
        actor: d.data.actor
      };
    });

    return flatten(
      compact(
        d.data.containers.map<RowData[] | null>((c) => {
          const user = d.data.users[c.userId];
          if (!user) {
            // should never happen
            return null;
          }
          const otherData: DataTable2RowOtherData = {
            searchData: { name: toFullName(user) },
            sortData: {
              completionPercentage: c.total.total ? c.total.percentage : null,
              preferredName: user?.preferredName || '',
              lastName: user?.lastName || ''
            }
          };
          const userShortName = shortName(user, 40);
          const userFullname = toFullName(user);
          const firstRowCells: DataTable2Cell[] = compact([
            {
              cell: (
                <NameContainer spacing={0.5}>
                  <div>
                    <WithTooltipIfTruncated
                      short={userShortName}
                      full={userFullname}
                    />
                  </div>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => {
                      trackingFns.openInstanceDialog(c.instanceId);
                      setDialogInstance({
                        tplId: c.templateId,
                        instanceId: c.instanceId
                      });
                    }}
                  >
                    <ExternalLink size={12} />
                  </IconButton>
                </NameContainer>
              )
            },
            {
              cell: <CompletionPercentageChip v={c.total} />
            },
            ...sortedTasks.map((tsk) => {
              const cell = gapOrAnchorOrOther(tsk, () => {
                // Potential to avoid data duplication?
                const status = c.byTask[tsk.id]?.status || null;
                // const status = c.topLevelRow.byTask[t.id]?.status || null;
                const task = resolvedTasksById[tsk.id];

                return (
                  <StatusTile
                    key={tsk.id}
                    status={status}
                    info={task}
                    onClick={() => {
                      trackingFns.trackBlockClick(
                        task,
                        taskStatusToString(status, t),
                        'status'
                      );
                      trackingFns.openExecutionDialog(task);
                      setPeopleDialogTask(task);
                    }}
                    onMouseEnter={() => {
                      trackingFns.trackBlockHover(
                        task,
                        taskStatusToString(status, t),
                        'status'
                      );
                    }}
                    hideActor
                  />
                );
              });
              return { cell };
            })
          ]);

          const firstRow: RowData = {
            key: c.instanceId,
            cells: firstRowCells,
            otherData
          };

          return [firstRow]; // could just return one here, but let's leave it in case we later want more expansion rows
        })
      )
    );
  }, [d, t, anchorPosition, anchorName, trackingFns, tpl?.data.title]);

  const rows = useApplySearchAndSortRows(allRows, search, sort);
  useErrorLogger(`drilldown report - ${type}: error`, error);

  if (d && d.type !== PARTICIPANT_ACTOR_KEY) {
    return null;
  }
  return (
    <>
      <div>
        {loading && <ReportDrilldownLoader />}
        {!loading && rows && !!rows.length && (
          <TableContainer>
            <DataTable2With2StickyColumns
              caption={'Completion for all tasks table'}
              sticky={{
                paddingRight: 1,
                firstColumnWidth: LEFT_COL_WIDTH
              }}
              rows={rows}
            />
          </TableContainer>
        )}
        {!loading && rows && !rows.length && <ReportDrilldownEmptyState />}
        {!loading && !rows && error && <ReportDrilldownError />}
      </div>

      {peopleDialogTask && d && (
        <CardItemDialog
          open={true}
          onClose={() => setPeopleDialogTask(null)}
          headerLeft={
            <Typography variant="h4">{getItemDisplayName(peopleDialogTask)}</Typography>
          }
          dialogTable={
            <TimelineItemExecutionTableLazy
              type="task"
              instanceIds={d.data.containers.map((c) => c.instanceId)}
              tplId={peopleDialogTask.tplId}
              actor={peopleDialogTask.actor}
              itemTplId={peopleDialogTask.id}
              onShowInstanceDetails={(inst) => {
                setDialogInstance({
                  tplId: inst.data.templateId,
                  instanceId: inst.id
                });
              }}
            />
          }
        />
      )}
      {dialogInstance && (
        <ParticipantDialogLazy
          open={true}
          onClose={() => setDialogInstance(null)}
          tplId={dialogInstance.tplId}
          instanceId={dialogInstance.instanceId}
        />
      )}
    </>
  );
};
